/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import {
  Button,
  DETAIL_ACTION_GHOST,
  DETAIL_ACTION_PRIMARY,
  DETAIL_ACTION_SECONDARY,
} from '@/shared/ui';
import { Tooltip } from '@/shared/ui/components/basic/Tooltip';
import { cn } from '@/shared/ui/utils/tailwind';
import { ACTIVE_PIPELINE_STAGES } from '../constants/deal-stages';
import type { DealViewState } from '../utils/deal-state';
import type { VisitAction } from '../utils/visit-action';

interface DealActionBarProps {
  view: DealViewState;
  /**
   * Whether the attendance has a property yet. Without one there is no
   * offer to record, so there is nothing to win on either.
   */
  canWin: boolean;
  onRecordContact: () => void;
  onScheduleVisit: () => void;
  /**
   * What the attendance's visits ask for next. `schedule` when nothing
   * is booked -- which includes an attendance whose only visit already
   * happened.
   */
  visitAction: VisitAction;
  /** Moves the outstanding visit forward. Unused when scheduling. */
  onSettleVisit: () => void;
  /** Opens the offer form, the primary from the proposal stage. */
  onRecordProposal: () => void;
  /** Switches to the checklist tab, the primary once in negotiation. */
  onOpenChecklist: () => void;
  onMarkWon: () => void;
  onPause: () => void;
  onLose: () => void;
  /**
   * Collapses Fechar negócio, Pausar and Abandonar behind one button.
   *
   * Only the phone passes it. Three ending actions plus a primary is four
   * controls across 400px, which either wraps to two rows or shrinks every
   * target below the thumb. Behind one button they get a sheet with room to
   * say what each one does — and "Abandonar" stops sitting beside the thing
   * somebody was aiming for.
   */
  onProgress?: () => void;
}

/** The primary button's label, its testid, its hint, and what it does. */
interface PrimaryAction {
  key: string;
  testId: string;
  run: () => void;
}

/**
 * The one action that moves this attendance from where it stands.
 *
 * Keyed off the stage, except across the two visit stages, where the
 * visits themselves decide: `visit_scheduled` is derived from a visit in
 * `pending`, `confirmed` *or* `done`, so three attendances wearing that
 * stage want three different actions — and offering "Agendar visita" to
 * all three invited a second booking on top of one still waiting to be
 * confirmed.
 *
 * @param props - The bar's props.
 * @returns The primary action to render.
 */
function primaryActionOf(props: DealActionBarProps): PrimaryAction {
  const { view } = props;
  if (view.stageIndex <= 0) {
    return { key: 'qualify', testId: 'qualify-deal-button', run: props.onRecordContact };
  }
  if (view.stageIndex <= 2) {
    return {
      key: `${props.visitAction}Visit`,
      testId: `${props.visitAction}-visit-button`,
      run: props.visitAction === 'schedule' ? props.onScheduleVisit : props.onSettleVisit,
    };
  }
  if (view.stageIndex === ACTIVE_PIPELINE_STAGES.length - 1) {
    return { key: 'recordProposal', testId: 'primary-record-proposal-button', run: props.onRecordProposal };
  }
  return { key: 'openChecklist', testId: 'open-checklist-button', run: props.onOpenChecklist };
}

/**
 * Action bar for a running deal: the stage's own next step first, the
 * cheapest fact beside it, and the ending actions past a rule.
 *
 * There is deliberately no Advance and no Rollback here. The stage is
 * derived from the facts the broker records -- a contact, a property of
 * interest, a booked visit, an offer and what became of it -- so a button
 * that moved the column by itself would let the stage disagree with what
 * actually happened. Correcting a stage means correcting the fact.
 *
 * "Fechar negócio" is not an exception to that. Accepting an offer *is*
 * the win, and this button does not go around it: it opens the offer form
 * and accepts what it records. A close agreed on the phone is a real
 * offer that was never written down, and the alternative -- a bare move
 * to `closed_won` -- wins the attendance on nothing, then opens a Venda
 * or a Locação with no number in it.
 *
 * @param props - The view state and one callback per action.
 * @returns The action bar, or nothing when the deal is not running.
 */
export function DealActionBar(props: DealActionBarProps) {
  const { view } = props;
  const { t } = useTranslation('features/deals');

  if (view.lifecycle !== 'running') return null;


  return (
    <div
      data-testid="deal-action-bar"
      className="flex flex-wrap items-center gap-(--space-2) sm:justify-end"
    >
      {/*
        No primary here, and no "Registrar contato" beside it.
        
        Both existed to move the funnel a step, and this bar does not move
        it: the stage is derived from the facts the broker records, so a
        button that advanced it by itself would let the stage disagree with
        what actually happened. The facts are recorded where they occur --
        a visit on the visits tab, an offer on the proposals tab -- and the
        stage follows. What is left here is what holds or ends the
        attendance.
      */}
      {props.onProgress ? (
        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_SECONDARY}
          onClick={props.onProgress}
          data-testid="deal-progress-button"
        >
          {t('detail.actions.progress')}
        </Button>
      ) : (
        <>
      {props.canWin && (
        <Tooltip label={t('detail.actionHints.markWon')}>
          <Button
            size="custom"
            variant="none"
            onClick={props.onMarkWon}
            className={DETAIL_ACTION_SECONDARY}
            data-testid="mark-won-button"
          >
            {t('detail.actions.markWon')}
          </Button>
        </Tooltip>
      )}

      {/*
        A rule between the action about values and the two that end the
        attendance: "Abandonar" must not sit under a thumb aiming for
        the one beside it.
      */}
      <div
        aria-hidden="true"
        className="mx-(--space-1) hidden h-5 w-px self-center bg-(--border-subtle) sm:block"
      />

      <Tooltip label={t('detail.actionHints.pause')}>
        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_GHOST}
          onClick={props.onPause}
          data-testid="pause-deal-button"
        >
          {t('detail.actions.pause')}
        </Button>
      </Tooltip>
      <Tooltip label={t('detail.actionHints.lose')}>
        <Button
          size="custom"
          variant="none"
          onClick={props.onLose}
          // Neutral at rest, red on approach. Abandoning is a decision,
          // not a failure, and #dc2626 sitting permanently in the row
          // was the loudest thing on a screen where nothing is wrong.
          className={cn(
            DETAIL_ACTION_GHOST,
            'hover:text-(--status-error-text) hover:bg-(--status-error-bg)',
          )}
          data-testid="lose-deal-button"
        >
          {t('detail.actions.lose')}
        </Button>
      </Tooltip>
        </>
      )}
    </div>
  );
}

interface DealStoppedActionsProps {
  view: DealViewState;
  onNewAttendance: () => void;
  onResume: () => void;
}

/**
 * The two actions an attendance that is no longer running still offers:
 * start a fresh one for the same client, or put this one back in motion.
 *
 * Paused and terminal share the slot because they ask the same question —
 * this is over, now what. Only the paused one offers the second action:
 * a closed attendance has no reopen, by design. Its outcome opened a
 * Venda or a Locação, and unwinding that from here would leave the two
 * records disagreeing; a client who comes back gets a fresh attendance.
 *
 * @param props - The view state and the two callbacks.
 * @returns The actions, or nothing while the deal still runs.
 */
export function DealStoppedActions({ view, onNewAttendance, onResume }: DealStoppedActionsProps) {
  const { t } = useTranslation('features/deals');
  if (view.lifecycle === 'running') return null;

  return (
    <div
      data-testid="deal-stopped-actions"
      className="flex flex-wrap items-center gap-(--space-2) sm:justify-end"
    >
      <Button
        size="custom"
        variant="none"
        className={DETAIL_ACTION_PRIMARY}
        onClick={onNewAttendance}
        data-testid="new-attendance-button"
      >
        {t('detail.actions.newAttendance')}
      </Button>
      {view.lifecycle === 'paused' && (
        <Button
          size="custom"
          variant="none"
          className={DETAIL_ACTION_SECONDARY}
          onClick={onResume}
          data-testid="resume-deal-button"
        >
          {t('detail.actions.resume')}
        </Button>
      )}
    </div>
  );
}
